<script>
import IconAngleLeft from "@js/components/Icons/IconAngleLeft.vue";
import VueEasyLightbox from "vue-easy-lightbox";
import IconZoomGlass from "@js/components/Icons/IconZoomGlass.vue";
import {Swiper, SwiperSlide} from "swiper/vue";
import ImageLoader from "@js/components/Loaders/ImageLoader.vue";

import "swiper/css";
import "swiper/css/pagination";
import {Pagination, Autoplay} from "swiper/modules";

export default {
    name: "BaseImageSlider",
    components: {
        ImageLoader,
        IconZoomGlass,
        VueEasyLightbox,
        IconAngleLeft,
        Swiper,
        SwiperSlide,
    },
    props: {
        images: {
            type: Array,
            required: true,
            default: () => [],
        },
        showThumbs: {
            type: Boolean,
            default: true,
        },
        thumbsDirection: {
            type: String,
            default: "horizontal",
            validator: (value) => ["horizontal", "vertical"].includes(value),
        },
        sliderHeightClass: {
            type: String,
            default: "aspect-square",
        },
    },
    data() {
        return {
            modules: [Pagination, Autoplay],
            is_preview_open: false,
            preview_key: 0,
            swiper: null,
            isMobile: false,
        };
    },
    computed: {
        hasImages() {
            return Array.isArray(this.images) && this.images.length > 0;
        },

        hasMultipleImages() {
            return this.images.length > 1;
        },

        effectiveThumbsDirection() {
            if (this.isMobile) {
                return "horizontal";
            }

            return this.thumbsDirection;
        },

        isHorizontalThumbs() {
            return this.effectiveThumbsDirection === "horizontal";
        },

        isVerticalThumbs() {
            return this.effectiveThumbsDirection === "vertical";
        },

        wrapperClasses() {
            if (this.isVerticalThumbs && this.showThumbs && this.hasMultipleImages) {
                return "flex gap-3 items-start";
            }

            return "block";
        },

        thumbsContainerClasses() {
            if (this.isVerticalThumbs) {
                return "thumbs-container thumbs-container-vertical flex flex-col space-y-2 p-1";
            }

            return "thumbs-container thumbs-container-horizontal flex space-x-2 mx-2 p-1";
        },

        mainSliderWrapperClasses() {
            if (this.isVerticalThumbs && this.showThumbs && this.hasMultipleImages) {
                return "flex-1 min-w-0";
            }

            return "w-full";
        },
    },
    mounted() {
        this.checkMobile();
        window.addEventListener("resize", this.checkMobile);
    },
    beforeUnmount() {
        window.removeEventListener("resize", this.checkMobile);
    },
    methods: {
        checkMobile() {
            this.isMobile = window.innerWidth < 768;
        },

        setSwiper(swiperInstance) {
            this.swiper = swiperInstance;
        },

        onSlideChange(swiperInstance) {
            this.preview_key = swiperInstance.realIndex ?? 0;
            this.$nextTick(() => this.scrollToActive());
        },

        openPreview(key) {
            this.preview_key = key;
            this.is_preview_open = true;
            this.$nextTick(() => this.scrollToActive());
        },

        setSlide(key) {
            if (!this.swiper) return;

            if (this.swiper.params.loop) {
                this.swiper.slideToLoop(key);
            } else {
                this.swiper.slideTo(key);
            }

            this.preview_key = key;
            this.$nextTick(() => this.scrollToActive());
        },

        nextSlide() {
            if (this.swiper) {
                this.swiper.slideNext();
            }
        },

        prevSlide() {
            if (this.swiper) {
                this.swiper.slidePrev();
            }
        },

        nextThumb() {
            this.nextSlide();
        },

        prevThumb() {
            this.prevSlide();
        },

        scrollToActive() {
            const container = this.$refs.thumbsContainer;
            if (!container) return;

            const active = container.children?.[this.preview_key];
            if (!active) return;

            if (this.isVerticalThumbs) {
                const containerHeight = container.clientHeight;
                const activeOffset = active.offsetTop;
                const activeHeight = active.clientHeight;
                const scrollPos = activeOffset - containerHeight / 2 + activeHeight / 2;

                container.scrollTo({
                    top: scrollPos,
                    behavior: "smooth",
                });

                return;
            }

            const containerWidth = container.clientWidth;
            const activeOffset = active.offsetLeft;
            const activeWidth = active.clientWidth;
            const scrollPos = activeOffset - containerWidth / 2 + activeWidth / 2;

            container.scrollTo({
                left: scrollPos,
                behavior: "smooth",
            });
        },
    },
};
</script>

<template>
    <div v-if="hasImages">
        <div :class="wrapperClasses">
            <div
                v-if="showThumbs && hasMultipleImages && isVerticalThumbs"
                class="hidden md:flex flex-col items-center shrink-0"
            >
                <button
                    @click.prevent="prevThumb"
                    class="p-1 w-16 rounded-t-2xl bg-gray-200 hover:bg-gray-300 transition"
                    type="button"
                >
                    <IconAngleLeft class="w-4 h-4 text-gray-600 rotate-90 mx-auto"/>
                </button>

                <div
                    ref="thumbsContainer"
                    :class="thumbsContainerClasses"
                    class="max-h-[24rem]"
                >
                    <img
                        v-for="(image, idx) in images"
                        :key="'vertical-thumb-' + idx"
                        :src="image"
                        @click.prevent="setSlide(idx)"
                        class="w-16 h-16 object-cover cursor-pointer rounded-sm transition-transform border"
                        :class="preview_key === idx
                            ? 'ring-2 ring-baseColor border-baseColor'
                            : 'opacity-70 hover:opacity-100 border-gray-200'"
                        :alt="'Картинка ' + (idx + 1)"
                    />
                </div>

                <button
                    @click.prevent="nextThumb"
                    class="p-1 w-16 rounded-b-2xl bg-gray-200 hover:bg-gray-300 transition"
                    type="button"
                >
                    <IconAngleLeft class="w-4 h-4 text-gray-600 -rotate-90 mx-auto"/>
                </button>
            </div>

            <div :class="mainSliderWrapperClasses">
                <div id="gallery" class="relative w-full z-1" data-carousel="static">
                    <Swiper
                        :modules="modules"
                        :slides-per-view="1"
                        :space-between="30"
                        @swiper="setSwiper"
                        @slideChange="onSlideChange"
                        :loop="hasMultipleImages"
                        :pagination="{ clickable: true }"
                        :autoplay="hasMultipleImages ? { delay: 5000, disableOnInteraction: true } : false"
                        class="relative overflow-hidden rounded-2xl border border-gray-100 shadow w-full"
                        :class="sliderHeightClass"
                    >
                        <SwiperSlide
                            v-for="(image, key) in images"
                            :key="'slide-' + key"
                            @click.prevent="openPreview(key)"
                            class="group cursor-pointer"
                        >
                            <div class="w-full h-full aspect-square">
                                <ImageLoader
                                    :src="image"
                                    :title="'Картинка ' + (key + 1)"
                                    class="w-full h-full"
                                    imgClasses="w-full h-full object-cover object-center"
                                />
                            </div>

                            <div
                                class="max-md:hidden absolute inset-0 p-4 text-white uppercase opacity-0 group-hover:opacity-100 bg-white/50 transition-all"
                            >
                                <div class="flex items-center justify-center w-full h-full">
                                    <IconZoomGlass/>
                                </div>
                            </div>
                        </SwiperSlide>
                    </Swiper>

                    <button
                        v-if="hasMultipleImages"
                        @click.prevent="prevSlide"
                        class="group absolute top-0 start-0 z-30 flex items-center justify-center h-full px-4 focus:outline-none"
                        type="button"
                    >
                        <span
                            class="inline-flex items-center justify-center w-10 h-10 rounded-full border-2 border-white bg-white/30 group-hover:bg-white/50 group-hover:border-baseColor transition"
                        >
                            <IconAngleLeft class="w-4 h-4 text-gray-800"/>
                        </span>
                    </button>

                    <button
                        v-if="hasMultipleImages"
                        @click.prevent="nextSlide"
                        class="group absolute top-0 end-0 z-30 flex items-center justify-center h-full px-4 focus:outline-none"
                        type="button"
                    >
                        <span
                            class="inline-flex items-center justify-center w-10 h-10 rounded-full border-2 border-white bg-white/30 group-hover:bg-white/50 group-hover:border-baseColor transition"
                        >
                            <IconAngleLeft class="w-4 h-4 text-gray-800 rotate-180"/>
                        </span>
                    </button>
                </div>
            </div>
        </div>

        <div
            v-if="showThumbs && hasMultipleImages && isHorizontalThumbs"
            class="mt-3 flex items-center"
        >
            <button
                @click.prevent="prevThumb"
                class="max-md:hidden p-1 h-16 rounded-l-2xl bg-gray-200 hover:bg-gray-300 transition"
                type="button"
            >
                <IconAngleLeft class="w-4 h-4 text-gray-600"/>
            </button>

            <div
                ref="thumbsContainer"
                :class="thumbsContainerClasses"
                class="w-full"
            >
                <img
                    v-for="(image, idx) in images"
                    :key="'horizontal-thumb-' + idx"
                    :src="image"
                    @click.prevent="setSlide(idx)"
                    class="w-16 h-16 object-cover cursor-pointer rounded-sm transition-transform border shrink-0"
                    :class="preview_key === idx
                        ? 'ring-2 ring-baseColor border-baseColor'
                        : 'opacity-70 md:hover:opacity-100 border-gray-200'"
                    :alt="'Картинка ' + (idx + 1)"
                />
            </div>

            <button
                @click.prevent="nextThumb"
                class="max-md:hidden p-1 h-16 rounded-r-2xl bg-gray-200 hover:bg-gray-300 transition"
                type="button"
            >
                <IconAngleLeft class="w-4 h-4 text-gray-600 rotate-180"/>
            </button>
        </div>

        <VueEasyLightbox
            :visible="is_preview_open"
            :imgs="images"
            :loop="true"
            :index="preview_key"
            @hide="is_preview_open = false"
            :moveDisabled="true"
        />
    </div>
</template>

<style scoped lang="scss">
.thumbs-container {
    -ms-overflow-style: none;
    scrollbar-width: none;

    &::-webkit-scrollbar {
        display: none;
    }
}

.thumbs-container-horizontal {
    overflow-x: auto;
    overflow-y: hidden;
}

.thumbs-container-vertical {
    overflow-y: auto;
    overflow-x: hidden;
}

.z-1 {
    z-index: 1;
}

:deep(.swiper-pagination-bullet) {
    background: #000;
    opacity: 0.3;
}

:deep(.swiper-pagination-bullet-active) {
    background: #000;
    opacity: 1;
}
</style>
